import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Badge } from 'react-bootstrap';
import Layout from '../components/Layout';
import apiService from '../services/api';

const STREAM_FIELDS = [
  { key: 'liveaudiovideo',        label: 'البث المرئي والصوتي',     icon: 'bi-play-circle-fill',   color: '#dc3545' },
  { key: 'liveaudiovideoyoutube', label: 'بث يوتيوب',               icon: 'bi-youtube',            color: '#ff0000' },
  { key: 'liveaudio',             label: 'البث الصوتي فقط',         icon: 'bi-mic-fill',           color: '#6f42c1' },
  { key: 'liveaudiovideodvrhls',  label: 'DVR – HLS',               icon: 'bi-camera-video-fill',  color: '#0d6efd' },
  { key: 'Liveaudiovideodvrdash', label: 'DVR – DASH',              icon: 'bi-camera-video-fill',  color: '#20c997' },
];

const breadcrumbs = [
  { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
  { label: 'البث المباشر', icon: 'bi-broadcast' },
];

const LiveStream = () => {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await apiService.getLive();
        console.log('[LiveStream] data:', data);
        setLive(data);
      } catch (err) {
        console.error('[LiveStream] error:', err.response || err);
        const msg = err.response?.data?.error?.message
          || err.response?.data?.message
          || err.message
          || 'تعذّر تحميل بيانات البث';
        setError(`${msg} (${err.response?.status ?? 'network error'})`);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  if (loading) {
    return (
      <Layout title="البث المباشر" breadcrumbs={breadcrumbs}>
        <Container className="text-center mt-5">
          <Spinner animation="border" role="status" />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="البث المباشر" breadcrumbs={breadcrumbs}>
      <Container fluid>

        {error && (
          <Alert variant="danger" className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle ms-2"></i>
            {error}
          </Alert>
        )}

        {!live && !error && (
          <Alert variant="info">
            <i className="bi bi-info-circle ms-2"></i>
            لا توجد بيانات بث متاحة حالياً.
          </Alert>
        )}

        {live && (
          <>
            {/* Status bar */}
            <div className="d-flex align-items-center gap-3 mb-4">
              <Badge bg="success" className="fs-6 px-3 py-2">
                <i className="bi bi-broadcast ms-1"></i>
                مباشر
              </Badge>
              <span className="text-muted small">
                آخر تحديث: {live.updatedAt ? new Date(live.updatedAt).toLocaleString('ar-SA') : '—'}
              </span>
            </div>

            {/* Stream URL cards */}
            <Row className="g-4">
              {STREAM_FIELDS.map(({ key, label, icon, color }) => {
                const url = live[key] || '';
                const hasUrl = url.trim() !== '';
                return (
                  <Col md={6} xl={4} key={key}>
                    <Card className="h-100 border-0 shadow-sm stream-card">
                      <Card.Body className="p-4">

                        {/* Card header */}
                        <div className="d-flex align-items-center mb-3">
                          <div
                            className="icon-circle me-3"
                            style={{ backgroundColor: color + '18', color }}
                          >
                            <i className={`bi ${icon}`} style={{ fontSize: '1.4rem' }}></i>
                          </div>
                          <div>
                            <div className="fw-bold fs-6">{label}</div>
                            <Badge
                              bg={hasUrl ? 'success' : 'secondary'}
                              className="mt-1"
                              style={{ fontSize: '0.7rem' }}
                            >
                              {hasUrl ? 'متاح' : 'غير مضبوط'}
                            </Badge>
                          </div>
                        </div>

                        {/* URL display */}
                        {hasUrl ? (
                          <div className="url-box d-flex align-items-center gap-2 p-2 rounded">
                            <span
                              className="text-truncate flex-grow-1 small text-muted"
                              title={url}
                              style={{ direction: 'ltr', textAlign: 'left' }}
                            >
                              {url}
                            </span>
                            <button
                              className="btn btn-sm btn-outline-secondary flex-shrink-0"
                              onClick={() => copyToClipboard(url, key)}
                              title="نسخ الرابط"
                            >
                              <i className={`bi ${copied === key ? 'bi-check2' : 'bi-clipboard'}`}></i>
                            </button>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary flex-shrink-0"
                              title="فتح الرابط"
                            >
                              <i className="bi bi-box-arrow-up-left"></i>
                            </a>
                          </div>
                        ) : (
                          <div className="text-muted small fst-italic">لا يوجد رابط</div>
                        )}

                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* Other non-URL fields */}
            <OtherFields live={live} />
          </>
        )}
      </Container>

      <style>{`
        .stream-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .stream-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 0.5rem 1.2rem rgba(0,0,0,0.12) !important;
        }
        .icon-circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .url-box {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          min-height: 42px;
        }
      `}</style>
    </Layout>
  );
};

const STREAM_KEYS = new Set([
  'liveaudiovideo', 'liveaudiovideoyoutube', 'liveaudio',
  'liveaudiovideodvrhls', 'Liveaudiovideodvrdash',
]);
const SYSTEM_KEYS = new Set([
  'id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt',
  'createdBy', 'updatedBy', 'locale',
]);

const OtherFields = ({ live }) => {
  const extras = Object.entries(live).filter(
    ([key]) => !STREAM_KEYS.has(key) && !SYSTEM_KEYS.has(key)
  );
  if (extras.length === 0) return null;

  return (
    <Card className="border-0 shadow-sm mt-4">
      <Card.Header className="bg-white fw-bold border-bottom">
        <i className="bi bi-info-circle ms-2"></i>
        معلومات إضافية
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          {extras.map(([key, value]) => (
            <Col sm={6} md={4} key={key}>
              <div className="text-muted small mb-1">{key}</div>
              <div className="fw-semibold">
                {value === null || value === undefined
                  ? '—'
                  : typeof value === 'boolean'
                  ? (value ? 'نعم' : 'لا')
                  : typeof value === 'object'
                  ? JSON.stringify(value)
                  : String(value)}
              </div>
            </Col>
          ))}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default LiveStream;
